"use client";

import { useLocale } from "next-intl";
import { useState } from "react";

import { normalizeImageAsset } from "@/app/utilities/images";
import { mapTagColor } from "@/app/utilities/mapTagColor";
import {
	BrandValuesStrip,
	FeatureCards,
	GiftHero,
	type FeatureCardCta,
	type FeatureCardTag,
	type FeatureCardTagColor
} from "@/composites/landing";

import {
	applyPlaceholders,
	type PlaceholderValues
} from "./apply-placeholders";
import { ClaimOfferForm, ClaimOutcome, TermsModal } from "./components";
import { formatOfferDate } from "./format-offer-date";
import {
	pickSection,
	type ClaimOfferPageData,
	type ClaimOutcomeContent,
	type OfferCard
} from "./types";
import type {
	GiftRecipientLandingActions,
	GiftRecipientLandingState
} from "./use-gift-recipient-landing";
import * as styles from "./GiftRecipientLandingView.module.scss";

export const SUBMIT_ERROR_MESSAGE =
	"Something went wrong on our end. Please try again in a moment.";

export interface GiftRecipientLandingViewProps
	extends GiftRecipientLandingState, GiftRecipientLandingActions {
	page: ClaimOfferPageData;
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function nonEmpty(value?: string | null): string | undefined {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
}

// Composites never touch window; the page owns navigation. A synthetic anchor
// avoids the popup block a react-aria press can trigger on window.open.
function openCta({ href, openInNewTab }: FeatureCardCta): void {
	if (!openInNewTab) {
		window.location.href = href;
		return;
	}
	const link = document.createElement("a");
	link.href = href;
	link.target = "_blank";
	link.rel = "noopener noreferrer";
	document.body.appendChild(link);
	link.click();
	link.remove();
}

function tagFor(card: OfferCard): FeatureCardTag | undefined {
	const label = nonEmpty(card.tag_label);
	if (!label) return undefined;
	return {
		label,
		color: card.tag_color
			? (mapTagColor(card.tag_color.toLowerCase()) as FeatureCardTagColor)
			: undefined,
		icon: card.tag_icon
	};
}

/**
 * Maps the entry plus the page state onto composites. The Terms modal is local
 * UI state so every state has a fetch-free story.
 */
export function GiftRecipientLandingView({
	page,
	status,
	recipient,
	values,
	errors,
	consultant,
	setField,
	submit
}: GiftRecipientLandingViewProps) {
	const locale = useLocale();
	const [termsOpen, setTermsOpen] = useState(false);

	const hasConsultant = Boolean(consultant.name);
	const offer = page.offer;
	const placeholders: PlaceholderValues = {
		consultantName: consultant.name,
		recipientFirstName: values.firstName.trim() || recipient.firstName,
		email: recipient.email,
		consultantPhone: consultant.phone,
		consultantEmail: consultant.email,
		discountLabel: offer?.discount_label,
		minimumSpend: offer?.minimum_spend,
		expirationDate: formatOfferDate(offer?.expiration_date, locale)
	};
	const fill = (text?: string): string =>
		applyPlaceholders(text, placeholders);
	const variant = (withConsultant?: string, without?: string): string =>
		fill(
			hasConsultant
				? withConsultant
				: (nonEmpty(without) ?? withConsultant)
		);

	const terms = offer?.terms;
	const termsBody = nonEmpty(terms?.body);
	const termsTitle = nonEmpty(terms?.modal_title) ?? "Terms & Conditions";

	const renderOutcome = (
		outcome: "success" | "ineligible",
		content: ClaimOutcomeContent | undefined,
		copyPlaceholders: PlaceholderValues
	) => {
		if (!content) return null;
		return (
			<div className={styles["gift-landing"]} data-state={outcome}>
				<ClaimOutcome
					outcome={outcome}
					iconImage={normalizeImageAsset(content.icon_image)}
					heading={fill(content.headline)}
					copy={applyPlaceholders(content.copy, copyPlaceholders)}
					followupHeading={fill(content.followup_heading)}
					followupCopy={variant(
						content.followup_copy,
						content.followup_copy_no_consultant
					)}
					consultantPhone={consultant.phone}
					consultantEmail={consultant.email}
					desktopImage={normalizeImageAsset(
						page.claim?.desktop_image
					)}
					mobileImage={normalizeImageAsset(page.claim?.mobile_image)}
				/>
			</div>
		);
	};

	if (status === "success") {
		return renderOutcome("success", page.claim?.success, placeholders);
	}

	if (status === "ineligible") {
		const emphasised = recipient.email
			? {
					...placeholders,
					email: `<strong>${escapeHtml(recipient.email)}</strong>`
				}
			: placeholders;
		return renderOutcome("ineligible", page.claim?.ineligible, emphasised);
	}

	const hero = pickSection(page.sections, "hero");
	const form = pickSection(page.sections, "form");
	const cards = pickSection(page.sections, "cards");
	const strip = pickSection(page.sections, "values");

	return (
		<div className={styles["gift-landing"]} data-state={status}>
			{hero ? (
				<GiftHero
					headline={fill(hero.headline)}
					copy={variant(hero.copy, hero.copy_no_consultant)}
					imageDesktopLeft={normalizeImageAsset(
						hero.hero_image_desktop_left
					)}
					imageDesktopRight={normalizeImageAsset(
						hero.hero_image_desktop_right
					)}
					imageMobile={normalizeImageAsset(hero.hero_image_mobile)}
				/>
			) : null}

			{form ? (
				<>
					<ClaimOfferForm
						sectionHeading={fill(form.eyebrow)}
						badge={form.badge}
						heading={fill(form.headline)}
						copy={variant(form.copy, form.copy_no_consultant)}
						desktopImage={normalizeImageAsset(
							form.desktop_image,
							form.headline
						)}
						mobileImage={normalizeImageAsset(
							form.mobile_image,
							form.headline
						)}
						email={recipient.email}
						emailHelperText={form.email_helper}
						values={values}
						errors={errors}
						ctaLabel={form.cta_label ?? "Claim My Offer"}
						consentCopy={fill(form.consent_copy)}
						termsLinkLabel={termsBody ? termsTitle : undefined}
						privacyPolicyLink={
							form.privacy_policy_link?.href
								? form.privacy_policy_link
								: undefined
						}
						benefitsHeading={form.benefits_heading}
						benefits={form.benefits
							?.map((item) => item.label?.trim() ?? "")
							.filter(Boolean)}
						isSubmitting={
							status === "checking" || status === "submitting"
						}
						submitError={
							status === "submit-error"
								? SUBMIT_ERROR_MESSAGE
								: undefined
						}
						onFieldChange={setField}
						onSubmit={() => void submit()}
						onTermsRequest={() => setTermsOpen(true)}
					/>
					{termsBody ? (
						<TermsModal
							isOpen={termsOpen}
							title={termsTitle}
							body={fill(termsBody)}
							buttonLabel={nonEmpty(terms?.acknowledge_label)}
							onClose={() => setTermsOpen(false)}
						/>
					) : null}
				</>
			) : null}

			{cards?.card?.length ? (
				<FeatureCards
					heading={fill(cards.eyebrow)}
					onCtaPress={openCta}
					cards={cards.card.map((card) => ({
						image: normalizeImageAsset(card.image, card.headline),
						tag: tagFor(card),
						heading: fill(card.headline),
						copy: fill(card.copy),
						cta: card.cta?.link?.href
							? {
									title: card.cta.link.title,
									href: card.cta.link.href,
									openInNewTab: card.cta.open_in_new_tab,
									assistiveText: nonEmpty(
										card.cta.assistive_text
									)
								}
							: undefined
					}))}
				/>
			) : null}

			{strip?.items?.length ? (
				<BrandValuesStrip
					heading={fill(strip.headline)}
					subcopy={fill(strip.copy)}
					items={strip.items.map((item) => ({
						image: normalizeImageAsset(item.image, item.title),
						title: item.title ?? "",
						caption: item.caption
					}))}
				/>
			) : null}
		</div>
	);
}
